import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';

const GURU_MENUS = [
  {
    label: 'Beranda',
    path: '/guru',
    desc: 'Halaman utama guru. Berisi tombol aksi cepat untuk mengakses fitur utama seperti Input Setoran, Absensi, Tasmi\', dan Ujian Tahfidz.',
  },
  {
    label: 'Setoran',
    path: '/guru/setoran',
    desc: 'Input hafalan setoran santri. Guru memilih santri, jenis setoran (Ziyadah, Murojaah, atau Binnadzar), juz, rentang halaman, dan jumlah halaman. Riwayat setoran tampil di bawah form dan bisa diedit atau dihapus.',
  },
  {
    label: "Tasmi'",
    path: '/guru/tasmi',
    desc: 'Input penilaian Tasmi\' (menyimak hafalan) santri. Guru memilih tipe (1 juz atau multi juz), penyimak, jumlah jali\', dan penilaian (A / A- / B / B- / Remedial). Kartu Tasmi\' bisa dicetak sebagai PDF.',
  },
  {
    label: 'Ujian Tahfidz',
    path: '/guru/ujian-tahfidz',
    desc: 'Input penilaian ujian tahfidz santri per semester. Terdiri dari 3 tab: Tajwid (satu nilai), MHQ (nilai per juz), dan Murojaah (juz, jumlah setoran, dan nilai). Data baru menggantikan data lama pada semester yang sama.',
  },
  {
    label: 'Chat',
    path: '/chat',
    desc: 'Fitur pesan langsung antara guru dan santri dalam halaqah. Guru dapat mengirim pesan ke santri binaannya, serta menghapus riwayat percakapan.',
  },
  {
    label: 'Rekap Absensi',
    path: '/guru/rekap',
    desc: 'Tabel rekap kehadiran santri per bulan. Menampilkan status H (Hadir), I (Izin), S (Sakit), A (Alpa), dan TS (Tidak Setor) per sesi (sore/malam). Bisa difilter per halaqah dan diekspor sebagai PDF.',
  },
  {
    label: 'Al-Quran Digital',
    path: '/quran',
    desc: 'Baca Al-Quran 114 surah lengkap dengan terjemahan bahasa Indonesia. Data diambil dari API alquran.cloud.',
  },
  {
    label: 'Tasbih Digital',
    path: '/tasbih',
    desc: 'Alat hitung dzikir digital. Ketuk layar untuk menambah hitungan. Tersedia target dan reset hitungan.',
  },
  {
    label: 'BQ News',
    path: '/berita',
    desc: 'Portal berita dan pengumuman pesantren. Guru dapat membaca artikel yang diterbitkan oleh admin. Setiap artikel mencatat jumlah tampilan.',
  },
  {
    label: 'Toko',
    path: '/toko',
    desc: 'Katalog produk pesantren (buku, seragam, dan lainnya). Pemesanan dilakukan via WhatsApp ke pesantren.',
  },
  {
    label: 'Donasi',
    path: '/donasi',
    desc: 'Halaman donasi untuk mendukung program pesantren. Guru dapat melihat program donasi aktif dan mengisi formulir donasi.',
  },
  {
    label: 'Profil Saya',
    path: '/guru/profil',
    desc: 'Pengaturan akun guru. Bisa mengganti foto profil dan mengubah password.',
  },
  {
    label: 'Pedoman Aplikasi',
    path: '/pedoman',
    desc: 'Halaman ini. Berisi panduan fungsi setiap menu dalam aplikasi ZahBaq untuk peran Guru dan Santri.',
  },
];

const SANTRI_MENUS = [
  {
    label: 'Beranda',
    path: '/santri',
    desc: 'Halaman utama santri. Menampilkan progress hafalan juz, setoran terakhir, dan tautan cepat ke fitur utama. Data diperbarui otomatis setiap 30 detik.',
  },
  {
    label: 'Riwayat Setoran',
    path: '/santri/setoran',
    desc: 'Daftar setoran hafalan yang telah diinput guru. Bisa difilter berdasarkan jenis: Semua, Ziyadah, Binnadzar, atau Murojaah.',
  },
  {
    label: "Tasmi'",
    path: '/santri/tasmi',
    desc: 'Lihat riwayat Tasmi\' yang telah dilakukan. Kartu Tasmi\' pribadi bisa ditampilkan dan diunduh sebagai PDF.',
  },
  {
    label: 'Progres',
    path: '/santri/progress',
    desc: 'Halaman progres hafalan lengkap. Menampilkan juz yang sudah selesai, sedang dihafal, dan total halaman setoran (Ziyadah, Murojaah, Binnadzar) sesuai urutan BQ (Juz 29 → 30 → 1 → dst).',
  },
  {
    label: 'Chat',
    path: '/chat',
    desc: 'Pesan langsung dengan guru halaqah. Santri dapat mengirim pertanyaan atau laporan hafalan kepada gurunya.',
  },
  {
    label: 'Ujian Tahfidz',
    path: '/santri/ujian-tahfidz',
    desc: 'Lihat hasil penilaian ujian tahfidz yang telah diinput guru. Menampilkan nilai Tajwid, MHQ, dan Murojaah per semester secara read-only.',
  },
  {
    label: 'Al-Quran Digital',
    path: '/quran',
    desc: 'Baca Al-Quran 114 surah lengkap dengan terjemahan bahasa Indonesia.',
  },
  {
    label: 'Tasbih Digital',
    path: '/tasbih',
    desc: 'Alat hitung dzikir digital. Ketuk untuk menambah hitungan, tersedia target dan reset.',
  },
  {
    label: 'BQ News',
    path: '/berita',
    desc: 'Berita dan pengumuman terkini dari pesantren.',
  },
  {
    label: 'Toko',
    path: '/toko',
    desc: 'Katalog produk pesantren. Pemesanan via WhatsApp.',
  },
  {
    label: 'Donasi',
    path: '/donasi',
    desc: 'Dukung program pesantren melalui donasi online. Tersedia pilihan program donasi yang aktif.',
  },
  {
    label: 'Profil Saya',
    path: '/santri/profil',
    desc: 'Pengaturan akun santri. Bisa mengganti foto profil dan mengubah password.',
  },
];

function Section({ title, color, menus }) {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <div className={`px-4 py-3 ${color}`}>
        <h2 className="font-bold text-base text-white">{title}</h2>
      </div>
      <div className="divide-y divide-gray-50">
        {menus.map((m, i) => (
          <div key={i}>
            <button
              data-testid={`pedoman-item-${m.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left">
              <div>
                <span className="text-sm font-semibold text-gray-800">{m.label}</span>
                <span className="text-xs text-gray-400 ml-2">{m.path}</span>
              </div>
              {openIdx === i
                ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" />
                : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
            </button>
            {openIdx === i && (
              <div className="px-4 pb-3">
                <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl p-3">
                  {m.desc}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PedomanPage() {
  const navigate = useNavigate();

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 pb-28 md:pb-8">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-950 to-emerald-800 px-4 pt-12 pb-6">
          <div className="flex items-center gap-3">
            <button data-testid="pedoman-back-btn" onClick={() => navigate(-1)}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
              <ArrowLeft size={20} className="text-white" />
            </button>
            <div>
              <h1 className="text-white font-bold text-lg">Pedoman Aplikasi</h1>
              <p className="text-emerald-300 text-xs">Panduan fungsi setiap menu ZahBaq</p>
            </div>
          </div>
        </div>

        <div className="px-4 py-4 space-y-4">
          <p data-testid="pedoman-intro" className="text-sm text-gray-500 bg-white rounded-2xl px-4 py-3 shadow-sm">
            Ketuk nama menu untuk membaca penjelasannya. Panduan ini berlaku untuk peran <strong>Guru</strong> dan <strong>Santri</strong>.
          </p>

          <Section
            title="Menu Guru"
            color="bg-gradient-to-r from-emerald-700 to-emerald-600"
            menus={GURU_MENUS}
          />

          <Section
            title="Menu Santri"
            color="bg-gradient-to-r from-teal-700 to-teal-600"
            menus={SANTRI_MENUS}
          />
        </div>
      </div>
    </Layout>
  );
}
